import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useB2BTranslation } from '../../hooks/useB2BTranslation';
import { createB2BValidationRules } from '../../validation/B2BValidationRules';

/**
 * Hook that combines form field component with validation
 * Provides a unified interface for B2B form fields
 */
export const useB2BFormField = (existingForm?: any) => {
  const { t } = useB2BTranslation();

  // Get validation rules
  const validationRules = createB2BValidationRules(t);

  // Always create a form instance (required by Rules of Hooks)
  const defaultForm = useForm({
    defaultValues: {},
    mode: 'onChange',
    criteriaMode: 'all'
  });

  // Use existing form if provided, otherwise use the default form
  // If existingForm is provided as individual methods, we need to use them directly
  // Otherwise use the default form
  const form = existingForm && typeof existingForm.register === 'function' ? existingForm : defaultForm;
  
  // Extract methods from existingForm if it's an object with individual methods
  const registerFn = existingForm?.register || form.register;
  const watchFn = existingForm?.watch || form.watch;
  const setValueFn = existingForm?.setValue || form.setValue;
  const clearErrorsFn = existingForm?.clearErrors || form.clearErrors;
  const triggerFn = existingForm?.trigger || form.trigger;
  const formState = existingForm?.formState || form.formState;

  // Convert B2B validation rules to react-hook-form format
  const getFieldRules = useCallback((fieldName: string) => {
    const rule = validationRules[fieldName];
    if (!rule) return {};

    const fieldRules: any = {};
    
    if (rule.required) {
      fieldRules.required = typeof rule.required === 'string' ? rule.required : 'This field is required';
    }
    
    if (rule.minLength) {
      fieldRules.minLength = {
        value: rule.minLength.value,
        message: rule.minLength.message || `Must be at least ${rule.minLength.value} characters`
      };
    }
    
    if (rule.maxLength) {
      fieldRules.maxLength = {
        value: rule.maxLength.value,
        message: rule.maxLength.message || `Must be less than ${rule.maxLength.value} characters`
      };
    }
    
    if (rule.pattern) {
      fieldRules.pattern = {
        value: rule.pattern.value,
        message: rule.pattern.message || 'Invalid format'
      };
    }
    
    if (rule.validate) {
      fieldRules.validate = rule.validate;
    }
    
    return fieldRules;
  }, [validationRules]);

  // Get field error message
  const getFieldError = useCallback((fieldName: string) => {
    const error = formState?.errors?.[fieldName];
    return error?.message || '';
  }, [formState?.errors]);

  // Get field value
  const getFieldValue = useCallback((fieldName: string) => {
    return watchFn ? watchFn(fieldName) : '';
  }, [watchFn]);

  // Set field value
  const setFieldValue = useCallback((fieldName: string, value: any, options?: { shouldValidate?: boolean; shouldDirty?: boolean }) => {
    if (setValueFn) {
      setValueFn(fieldName, value, options);
    }
  }, [setValueFn]);

  // Clear field error
  const clearFieldError = useCallback((fieldName: string) => {
    if (clearErrorsFn) {
      clearErrorsFn(fieldName);
    }
  }, [clearErrorsFn]);

  // Validate single field
  const validateField = useCallback(async (fieldName: string) => {
    if (triggerFn) {
      const result = await triggerFn(fieldName);
      return result;
    }
    return true;
  }, [triggerFn]);

  // Get field props for B2BFormField component
  const getFieldProps = useCallback((fieldName: string, fieldType: string, additionalProps: any = {}) => {
    const value = getFieldValue(fieldName);
    const error = getFieldError(fieldName);
    let rules = getFieldRules(fieldName);
    
    // Merge additionalProps.required with existing rules
    // If required is passed in additionalProps, ensure it's in the validation rules
    if (additionalProps.required) {
      rules = {
        ...rules,
        required: typeof additionalProps.required === 'string' 
          ? additionalProps.required 
          : (rules.required || 'This field is required')
      };
    }

    // Register the field with react-hook-form for proper validation
    // This ensures validation rules are applied during form submission
    // Note: register() is safe to call on every render - react-hook-form handles this internally
    const registeredField = registerFn ? registerFn(fieldName, rules) : { ref: null, onChange: null, onBlur: null };
    
    // Strip Cyrillic (Russian etc.) to enforce Latin-only input when requested
    const stripCyrillic = (str: string): string => str.replace(/[\u0400-\u04FF]/g, "");

    // Our custom onChange handler that works with B2BFormField's (name, value) signature
    // but also calls the registered field's onChange for react-hook-form
    const handleChange = (name: string, newValue: any) => {
      if (additionalProps.latinOnly && typeof newValue === "string") {
        newValue = stripCyrillic(newValue);
      }
      // Update the value using setValue for our custom handler
      setFieldValue(name, newValue, { shouldValidate: true, shouldDirty: true });
      clearFieldError(name);

      // Also trigger the registered field's onChange if it exists
      if (registeredField.onChange) {
        const syntheticEvent = {
          target: { name, value: newValue },
          currentTarget: { name, value: newValue }
        } as any;
        registeredField.onChange(syntheticEvent);
      }
    };

    // Create a wrapper for onBlur that works with both systems
    const handleBlurWrapper = (nameOrEvent?: string | React.FocusEvent<HTMLElement>) => {
      // Always call react-hook-form's onBlur first if it exists
      // This ensures react-hook-form tracks the blur state
      if (registeredField?.onBlur) {
        try {
          // Check if we have a valid event object with target property
          const isEvent = nameOrEvent && 
                         typeof nameOrEvent === 'object' && 
                         'target' in nameOrEvent && 
                         nameOrEvent.target !== null &&
                         nameOrEvent.target !== undefined;
          
          if (isEvent) {
            // Called with a FocusEvent from native input
            registeredField.onBlur(nameOrEvent as any);
          } else {
            // Called without event or with string - create a synthetic event
            const currentValue = getFieldValue(fieldName);
            const syntheticEvent = {
              target: { 
                name: fieldName, 
                value: currentValue || '',
                id: fieldName
              },
              currentTarget: { 
                name: fieldName, 
                value: currentValue || '',
                id: fieldName
              },
              type: 'blur'
            } as any;
            registeredField.onBlur(syntheticEvent);
          }
        } catch (error) {
          // Silently handle errors in onBlur to prevent breaking the form
          console.warn('Error in onBlur handler:', error);
        }
      }
      
      // Then run our validation
      const fieldToValidate = typeof nameOrEvent === 'string' ? nameOrEvent : fieldName;
      validateField(fieldToValidate);
    };

    // Return props - Include registration for proper validation
    return {
      name: fieldName,
      type: fieldType,
      value: fieldType === 'checkbox' ? Boolean(value) : (value || ''),
      error,
      // Our custom onChange handler (takes name and value, not event)
      onChange: handleChange,
      // Our custom onBlur handler
      onBlur: handleBlurWrapper,
      // Include ref for react-hook-form registration
      ref: registeredField.ref,
      ...additionalProps
    };
  }, [getFieldValue, getFieldError, getFieldRules, setFieldValue, clearFieldError, validateField, registerFn]);

  return {
    ...form,
    validationRules,
    getFieldRules,
    getFieldError,
    getFieldValue,
    setFieldValue,
    clearFieldError,
    validateField,
    getFieldProps
  };
};

export default useB2BFormField;
